import constants from '../Constants/constants';
import { cacheCardNames, normalizeQuery, fuzzyMatchCardNames } from './cardNameCache';

// Filter option constants
export const ELEMENT_NAMES = ["Fire", "Water", "Terra", "Air", "Plasma", "Necro"];
export const RARITY_OPTIONS = ["Legendary Rare", "Secret Rare", "Super Rare", "Rare", "Uncommon", "Common", "Promo", "Starter"];
export const CARD_TYPES = ["Character", "Ability", "Strategy", "Field"];
export const TURN_COUNTS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
export const ABILITY_COSTS = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];
export const COMBAT_ATTRIBUTES = ["Overworld", "Dragon", "Pirate", "Micromon", "Pleaguis", "Arizal", "Bio", "Bethesda", "Mecha", "Kaiser", "Circus", "Navy", "Undead", "Sin", "Golem", "Kalypto"];
export const CARD_SETS = ["Awakened Primordials", "Battlers of Bethesda", "Warden of the Seven Deadly Sins", "New Genesis"];

/**
 * Build an API endpoint URL for searching cards.
 * @param {Object} filters - Filter state object
 * @param {string[]} cardTypes - Specific card types for this query
 * @param {boolean} includeTurnCount - Whether to include turn count filter
 * @param {string} [searchOverride] - Override search term (used for fuzzy retries)
 * @returns {string} The constructed endpoint URL
 */
export function buildEndpoint(filters, cardTypes, includeTurnCount, searchOverride) {
  const {
    search, characteristic, cardEffect, rarityQuery,
    turnCountQuery, combatQuery, cardSetQuery
  } = filters;

  const searchTerm = searchOverride !== undefined ? searchOverride : (search || '').trim();

  let endpoint = `${constants.DOMAIN}api/v1/guest/post?`;

  if (searchTerm) {
    endpoint += `search=${encodeURIComponent(searchTerm)}&`;
  }
  if (characteristic && characteristic.trim()) {
    endpoint += `characteristic=${encodeURIComponent(characteristic.trim())}&`;
  }
  if (cardEffect && cardEffect.trim()) {
    endpoint += `cardEffect=${encodeURIComponent(cardEffect.trim())}&`;
  }
  if (rarityQuery && rarityQuery.length > 0) {
    endpoint += `rarity=${encodeURIComponent(rarityQuery.join("|"))}&`;
  }
  if (cardTypes && cardTypes.length > 0) {
    endpoint += `type=${encodeURIComponent(cardTypes.join("|"))}&`;
  }
  if (includeTurnCount && turnCountQuery && turnCountQuery.length > 0) {
    endpoint += `turnCount=${encodeURIComponent(turnCountQuery.join("|"))}&`;
  }
  if (combatQuery && combatQuery.length > 0) {
    endpoint += `skill=${encodeURIComponent(combatQuery.join("|"))}&`;
  }
  if (cardSetQuery && cardSetQuery.length > 0) {
    endpoint += `cardSet=${encodeURIComponent(cardSetQuery.join("|"))}&`;
  }

  return endpoint;
}

/**
 * Fetch cards from an endpoint, handling element queries correctly.
 */
async function fetchWithElements(endpoint, elementsQuery, dualElementsOnly) {
  if (!elementsQuery || elementsQuery.length === 0) {
    const res = await fetch(endpoint, { mode: 'cors', method: 'GET' });
    const data = await res.json();
    return data.posts || [];
  } else if (dualElementsOnly && elementsQuery.length >= 2) {
    const url = endpoint + `element=${encodeURIComponent(elementsQuery.join("|"))}&`;
    const res = await fetch(url, { mode: 'cors', method: 'GET' });
    const data = await res.json();
    let results = data.posts || [];
    results = results.filter(card => {
      if (!card.element) return false;
      return elementsQuery.every(el => card.element.includes(el));
    });
    return results;
  } else {
    const allResults = [];
    for (const element of elementsQuery) {
      const url = endpoint + `element=${encodeURIComponent(element)}&`;
      const res = await fetch(url, { mode: 'cors', method: 'GET' });
      const data = await res.json();
      allResults.push(...(data.posts || []));
    }
    return allResults;
  }
}

/**
 * Perform a full card search with all filters, deduplication, client-side filtering,
 * and adaptive fuzzy search fallback.
 *
 * @param {Object} filters - All filter state
 * @param {Object} [options] - Additional options
 * @param {Function} [options.getAbilityCostCount] - Function to count ability cost
 * @param {Function} [options.getAbilityCostElements] - Function to get ability cost elements
 * @returns {Promise<{posts: Array, suggestions: string[]}>}
 */
export async function performCardSearch(filters, options = {}) {
  const {
    search, elementsQuery, typeQuery, turnCountQuery,
    abilityCostQuery, abilityCostElementQuery, dualElementsOnly,
    hideKickstarter, rarityQuery
  } = filters;

  const { getAbilityCostCount, getAbilityCostElements } = options;

  let posts = [];
  const seenIds = new Set();

  const turnCountTypes = (typeQuery || []).filter(t => t === "Character" || t === "Strategy");
  const abilityCostTypes = (typeQuery || []).filter(t => t === "Ability");
  const otherTypes = (typeQuery || []).filter(t => t === "Field");

  const hasMixedTypes = (typeQuery || []).length > 0 &&
    ((turnCountTypes.length > 0 && (abilityCostTypes.length > 0 || otherTypes.length > 0)) ||
     (abilityCostTypes.length > 0 && otherTypes.length > 0));

  const needsSeparateTypeQueries = hasMixedTypes &&
    ((turnCountQuery || []).length > 0 || (abilityCostQuery || []).length > 0);

  if (needsSeparateTypeQueries) {
    if (turnCountTypes.length > 0) {
      const endpoint = buildEndpoint(filters, turnCountTypes, true);
      const results = await fetchWithElements(endpoint, elementsQuery, dualElementsOnly);
      for (const post of results) {
        if (!seenIds.has(post._id)) { seenIds.add(post._id); posts.push(post); }
      }
    }
    if (abilityCostTypes.length > 0) {
      const endpoint = buildEndpoint(filters, abilityCostTypes, false);
      const results = await fetchWithElements(endpoint, elementsQuery, dualElementsOnly);
      for (const post of results) {
        if (!seenIds.has(post._id)) { seenIds.add(post._id); posts.push(post); }
      }
    }
    if (otherTypes.length > 0) {
      const endpoint = buildEndpoint(filters, otherTypes, false);
      const results = await fetchWithElements(endpoint, elementsQuery, dualElementsOnly);
      for (const post of results) {
        if (!seenIds.has(post._id)) { seenIds.add(post._id); posts.push(post); }
      }
    }
  } else {
    const includeTurnCount = (typeQuery || []).length === 0 ||
      (typeQuery || []).every(t => t === "Character" || t === "Strategy");

    const endpoint = buildEndpoint(
      filters,
      (typeQuery || []).length > 0 ? typeQuery : null,
      includeTurnCount
    );

    posts = await fetchWithElements(endpoint, elementsQuery, dualElementsOnly);
  }

  // Remove duplicates
  const uniquePosts = [];
  const finalSeenIds = new Set();
  for (const post of posts) {
    if (!finalSeenIds.has(post._id)) { finalSeenIds.add(post._id); uniquePosts.push(post); }
  }
  posts = uniquePosts;

  // Cache card names from every API response for fuzzy search
  cacheCardNames(posts);

  // Client-side filter for Ability Cost
  if (abilityCostQuery && abilityCostQuery.length > 0 && getAbilityCostCount) {
    posts = posts.filter(card => {
      if (card.cardType !== "Ability") return true;
      const costCount = getAbilityCostCount(card);
      return abilityCostQuery.includes(String(costCount));
    });
  }

  // Client-side filter for Ability Cost Element
  if (abilityCostElementQuery && abilityCostElementQuery.length > 0 && getAbilityCostElements) {
    posts = posts.filter(card => {
      if (card.cardType !== "Ability") return true;
      if (!card.abilityCost) return false;

      const costItems = Array.isArray(card.abilityCost)
        ? card.abilityCost
        : card.abilityCost.split(',').map(s => s.trim());

      const hasGenericCost = costItems.some(item => {
        const lower = item.toLowerCase();
        return lower === 'x' || !isNaN(parseInt(item, 10));
      });
      if (hasGenericCost) return false;

      const costElements = getAbilityCostElements(card);
      return abilityCostElementQuery.some(el => costElements.includes(el.toLowerCase()));
    });
  }

  // Client-side exact match filter for rarity
  if (rarityQuery && rarityQuery.length > 0) {
    posts = posts.filter(card => rarityQuery.includes(card.rarity));
  }

  // Filter out Kickstarter and Promo Pack cards
  if (hideKickstarter) {
    posts = posts.filter(card => {
      const cardSet = card.cardSet?.toLowerCase() || '';
      return !cardSet.includes('kickstarter') && !cardSet.includes('2023 promo pack');
    });
  }

  // Adaptive fuzzy search
  if (posts.length === 0 && search && search.trim()) {
    const original = search.trim();
    const normalized = normalizeQuery(original);

    // Try normalized form
    if (normalized && normalized.toLowerCase() !== original.toLowerCase()) {
      try {
        const retryEndpoint = buildEndpoint(
          filters,
          (typeQuery || []).length > 0 ? typeQuery : null,
          (typeQuery || []).length === 0 ||
            (typeQuery || []).every(t => t === "Character" || t === "Strategy"),
          normalized
        );
        const retryRes = await fetch(retryEndpoint, { mode: 'cors', method: 'GET' });
        const retryData = await retryRes.json();
        const retryPosts = retryData.posts || [];
        if (retryPosts.length > 0) {
          cacheCardNames(retryPosts);
          return { posts: retryPosts, suggestions: [] };
        }
      } catch {
        // Continue to fuzzy
      }
    }

    // Fuzzy match
    const suggestions = fuzzyMatchCardNames(original);
    if (suggestions.length > 0) {
      return { posts: [], suggestions };
    }
  }

  return { posts, suggestions: [] };
}
